import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaTrash, FaUndo } from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import { getUserRole } from "../../Utils/role";
import Pagination from '../../common/Pagination';
import {
    getDeletedLeadList,
    permanentDeleteLead,
    restoreLead
} from "../../redux/services/leadServices/Lead.Services";

function AdminDeletedLeadList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const role = getUserRole();
    const [leadData, setLeadData] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);

    const [totalPages, setTotalPages] = useState(1);
    const [totalLeads, setTotalLeads] = useState(0);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
    const [counts, setCounts] = useState({ all: 0 });

    // SELECTION STATES
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);

    useEffect(() => {
        fetchLeads(1, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchLeads = async (pageNo = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) setInitialLoading(true);
            else setTableLoading(true);

            const payload = {
                page: pageNo,
                per_page: limit,
            };

            const response = await getDeletedLeadList(payload);

            const leads = (response?.data || []);
            setLeadData(leads);
            setFilteredLeads(leads);

            setPage(response?.current_page || 1);
            setTotalPages(response?.last_page || 1);
            setTotalLeads(response?.total || 0);
            setCounts({ all: response?.total || 0 });

            // Reset selection
            setSelectedIds([]);

        } catch (error) {
            console.log(error);
            errorToast("Failed to fetch deleted leads");
        } finally {
            setInitialLoading(false);
            setTableLoading(false);
        }
    };

    // ✅ FIXED: Single Restore (Sending Array format as backend expects)
    const handleRestore = async (item) => {
        try {
            setTableLoading(true);
            // Backend expects { ids: [1, 2] } format
            const res = await restoreLead({ ids: [item.id] });

            if (res?.success) {
                successToast(res?.message || "Lead restored successfully");
                fetchLeads(page);
            } else {
                errorToast(res?.message || "Failed to restore lead");
            }
        } catch (error) {
            console.log(error);
            errorToast(error?.response?.data?.message || "Failed to restore lead");
        } finally {
            setTableLoading(false);
        }
    };

    // Checkbox Handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(leadData.map((item) => item.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    // ✅ FIXED: Bulk Restore (Sending all selected IDs at once)
    const handleBulkRestore = async () => {
        if (selectedIds.length === 0) return;
        try {
            setIsBulkProcessing(true);

            // Backend expects { ids: [1, 2, 3] } format
            const res = await restoreLead({ ids: selectedIds });

            if (res?.success) {
                successToast(res?.message || `${selectedIds.length} leads restored successfully`);
                setSelectedIds([]);
                fetchLeads(page);
            } else {
                errorToast(res?.message || "Failed to restore selected leads");
            }
        } catch (error) {
            console.log(error);
            errorToast(error?.response?.data?.message || "Failed to restore selected leads");
        } finally {
            setIsBulkProcessing(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
            setPage(newPage);
            fetchLeads(newPage);
        }
    };

    if (initialLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary"></div>
            </div>
        );
    }

    const isAllSelected = leadData.length > 0 && selectedIds.length === leadData.length;

    return (
        <div>
            {/* HEADER */}
            <div className="d-flex align-items-center justify-content-between page-header-breadcrumb">
                <div className="title_member">
                    <h4>Deleted Leads</h4>
                    <nav className="page-breadcrumb">
                        <Link to="/">Home</Link>
                        <span className="mx-2">/</span>
                        <span>Deleted Leads</span>
                    </nav>
                </div>

                {/* BULK ACTION BUTTONS */}
                {selectedIds.length > 0 && (
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-success d-flex align-items-center gap-2"
                            onClick={handleBulkRestore}
                            disabled={isBulkProcessing || tableLoading}
                        >
                            {isBulkProcessing ? <span className="spinner-border spinner-border-sm"></span> : <FaUndo />}
                            Restore Selected ({selectedIds.length})
                        </button>

                        <button
                            className="btn btn-danger d-flex align-items-center gap-2"
                            onClick={() => {
                                dispatch(
                                    openModal({
                                        type: "delete-lead",
                                        title: "Permanently Delete Leads",
                                        data: {
                                            name: `${selectedIds.length} selected leads`,
                                            // Loop to call single delete API for each selected ID
                                            deleteFunction: async () => {
                                                const promises = selectedIds.map(id => permanentDeleteLead({ id }));
                                                return await Promise.all(promises);
                                            },
                                            refreshData: () => {
                                                setSelectedIds([]);
                                                fetchLeads(page);
                                            },
                                        },
                                    })
                                );
                            }}
                            disabled={isBulkProcessing || tableLoading}
                        >
                            <FaTrash /> Delete Selected ({selectedIds.length})
                        </button>
                    </div>
                )}
            </div>

            <div className='main_page'>
                {totalLeads > limit && (
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
                        <div className="col-12 d-flex justify-content-end">
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>
                )}

                <div className="st-wrapper-card">
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    {/* SELECT ALL CHECKBOX */}
                                    <th style={{ width: "40px" }}>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={isAllSelected}
                                            onChange={handleSelectAll}
                                            disabled={leadData.length === 0 || tableLoading || isBulkProcessing}
                                            style={{ cursor: "pointer" }}
                                        />
                                    </th>
                                    <th>Sr.No.</th>
                                    <th>Lead Id</th>
                                    <th>Name</th>
                                    <th>Mobile No</th>
                                    <th>Source</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableLoading ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="spinner-border text-primary"></div>
                                        </td>
                                    </tr>
                                ) : filteredLeads?.length > 0 ? (
                                    filteredLeads.map((item, index) => (
                                        <tr key={item.id}>
                                            {/* ROW CHECKBOX */}
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={selectedIds.includes(item.id)}
                                                    onChange={() => handleSelectOne(item.id)}
                                                    disabled={isBulkProcessing}
                                                    style={{ cursor: "pointer" }}
                                                />
                                            </td>
                                            <td>{(page - 1) * limit + index + 1}</td>
                                            <td>{item.lead_id}</td>
                                            <td className="fw-semibold">{item?.name}</td>
                                            <td>{item.mobile}</td>
                                            <td>{item.source}</td>

                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-1">

                                                    {/* VIEW */}
                                                    <button
                                                        className="st-action-btn st-view-btn"
                                                        title="View Lead"
                                                        onClick={() => navigate(`/view-lead/${item.id}`)}
                                                        disabled={isBulkProcessing}
                                                    >
                                                        <FaEye />
                                                    </button>

                                                    {/* SINGLE RESTORE */}
                                                    <button
                                                        className="st-action-btn st-edit-btn text-success"
                                                        title="Restore Lead"
                                                        onClick={() => handleRestore(item)}
                                                        disabled={tableLoading || isBulkProcessing}
                                                    >
                                                        <FaUndo />
                                                    </button>

                                                    {/* SINGLE PERMANENT DELETE */}
                                                    {role === "admin" && (
                                                        <button
                                                            className="st-action-btn st-delete-btn text-danger"
                                                            title="Permanently Delete"
                                                            onClick={() =>
                                                                dispatch(
                                                                    openModal({
                                                                        type: "delete-lead",
                                                                        title: "Permanently Delete Lead",
                                                                        data: {
                                                                            id: item.id,
                                                                            name: item.name,
                                                                            deleteFunction: permanentDeleteLead,
                                                                            refreshData: () => fetchLeads(page),
                                                                        },
                                                                    })
                                                                )
                                                            }
                                                            disabled={tableLoading || isBulkProcessing}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={7}
                                            style={{
                                                textAlign: "center",
                                                verticalAlign: "middle",
                                                width: "100%",
                                                padding: "40px 0",
                                            }}
                                        >
                                            <h5 className="" style={{ textAlign: "center" }}>
                                                No deleted leads found
                                            </h5>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {totalLeads > limit && (
                            <div className="d-flex justify-content-end mt-3">
                                <Pagination
                                    page={page}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminDeletedLeadList;